import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'

import {
  Box,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { GenerationTooltipContent } from '~/components/balance/GenerationTooltip'
import type { GenerationTooltipKoma } from '~/components/balance/GenerationTooltip'
import { GENERATION_DETAIL_SERIES_COLORS } from '~/constants/chartPalette'
import { useJstMidnightTick } from '~/hooks/useJstMidnightTick'
import { formatChartTick } from '~/models/chartFormatting'
import { isTargetDateTodayInJst, toProtoDate } from '~/models/dateTime'
import { komaNumberToTime } from '~/models/komaToTime'
import type { GenDetailSeriesVisibility } from '~/pages/BalanceGenerationDetail/constants'
import type { HatsuDetailKomaData } from '~/pages/BalanceGenerationDetail/models/convertGenerationDetailResponse'

/** チャート右マージン。 */
const CHART_MARGIN_RIGHT = 4
/**
 * チャート左マージン。 **負値にしない**。
 *
 * 従来 `-15` で、 Y 軸ラベルが 4 桁以上 (例 `1,400`) になると先頭が軸幅からはみ出て
 * 「400」 としか読めなくなっていた (2026-08-25 実測)。 需要詳細の DetailChart は
 * 同じ理由で既に 0 に直してあり (「負値にすると需要の 5〜6 桁 MWh 目盛りラベルが
 * 左端で見切れる」)、 発電側だけ取り残されていた。 同時同量 3 チャートで
 * プロット左端も揃う。
 */
const CHART_MARGIN_LEFT = 0

type Props = {
  data: HatsuDetailKomaData[]
  gcKoma: number
  /** 表示中の日付。当日以外は GC 縦線と GC ラベルを描画しない
   *  (spec AC-BDH-019a「過去日・未来日は非表示」。需要側 DetailChart の
   *  AC-BDB-022a と同じ扱い)。 */
  displayDate: Date
  /** 基本表示トグル (design hatsu-detail.html 基本表示 switch) の表示状態。 */
  seriesVisibility: Pick<GenDetailSeriesVisibility, 'genPlan' | 'genActual'>
}

export const DetailChart = ({
  data,
  gcKoma,
  displayDate,
  seriesVisibility,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const gcTime = komaNumberToTime(gcKoma + 1)
  // AC-BDH-019a: GC 縦線は当日のみ。 従来は日付に関係なく引きっぱなしで、
  // 過去日を見ている時も「今日の GC 位置」 の線が出ていた (#2941 でラベルを
  // 足すと、 その誤りがラベル付きでさらに目立つため同時に閉じる)。
  //
  // 当日判定は render 時計算なので、 画面を開いたまま JST 0:00 を跨ぐと
  // 「昨日になった日付」 に GC 線が出たまま残る。 一覧の 5 秒ポーリングでは
  // 直らない: 過去日になった受渡日のレスポンスは以後変化せず、 React Query の
  // structural sharing で再 render が起きないため。 [[useJstMidnightTick]] で
  // 日跨ぎ時に 1 回だけ再 render させる (codex 指摘 2026-08-25)。
  useJstMidnightTick()
  const isTodayDisplay = isTargetDateTodayInJst(toProtoDate(displayDate))

  /* tooltip の中身は発電ダッシュボード (`BgChart`) と共有する。 design も
     `renderHatsuChart` 1 本を両画面で呼んでおり、 需要側も `DemandTooltip` を
     ダッシュボードと詳細で共有している (#7693)。 */
  const renderTooltipContent = useCallback(
    (props: TooltipContentProps) => (
      <GenerationTooltipContent
        {...(props as TooltipContentProps<number, string>)}
        t={t}
      />
    ),
    [t],
  )

  const axisTickStyle = {
    fontSize: 11,
    fill: LightModeSemanticColors.Text.midEmphasis.value,
  }

  // design charts.js renderHatsuChart: 計画線 = brand-cyan / 実績線 = text-positive-mid
  const planColor = GENERATION_DETAIL_SERIES_COLORS.genPlan
  const actualColor = GENERATION_DETAIL_SERIES_COLORS.genActual

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '6px',
        background: LightModeSemanticColors.Surface.primary.value,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Axis labels */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '1px 4px 0 0',
          fontSize: fontSizes.xs.value,
          fontWeight: 600,
          color: LightModeSemanticColors.Text.midEmphasis.value,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <span>{t('page:balanceGenerationDetail.chart.kwh')}</span>
      </div>

      {/* Chart */}
      {/*
       * raw <div> ではなく Chakra Box に変える: css prop で recharts の目盛
       * テキストを 11px に固定するため (Chakra preflight の
       * `"*": { font: "inherit" }` が全要素に効き、Recharts の
       * `tick={{ fontSize }}` = SVG の presentation 属性が body 継承値 (14px) に
       * 負けて効かない。Supply48DataChart.tsx と同じ罠。値の出所は
       * `axisTickStyle` / GC ラベルの `fontSize`/`fontWeight` のままにし、
       * 効かせ方だけ css 経由に変える)。
       * GC ラベル (ReferenceLine の label) も同じ Text component で描かれ
       * `.recharts-label` class を持つため、`font-weight` も同様に preflight の
       * `font` ショートハンドで normal に戻る — こちらは `.recharts-label` を
       * 個別 selector にして太字だけ狙い撃ちで戻す (軸目盛りの weight は変えない)。
       */}
      <Box
        flex={1}
        minHeight="200px"
        css={{
          '& .recharts-text': { fontSize: '11px' },
          '& .recharts-label': { fontWeight: 700 },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            margin={{
              top: 12,
              right: CHART_MARGIN_RIGHT,
              left: CHART_MARGIN_LEFT,
              bottom: 2,
            }}
            data={data satisfies GenerationTooltipKoma[]}
          >
            <CartesianGrid
              stroke={LightModeSemanticColors.Border.lowEmphasis.value}
            />
            <XAxis
              dataKey="time"
              interval={0}
              tick={axisTickStyle}
              tickFormatter={formatChartTick}
            />
            <YAxis
              width={52}
              tick={axisTickStyle}
              tickFormatter={(v: number) => Math.round(v).toLocaleString()}
            />
            {/* 既定 tooltip を潰したまま代わりが無く、 ホバーしても
                cursor の網掛けが出るだけで値が読めなかった (#7693 / #7064)。
                spec `doji-hatsu.md`「ツールチップで各コマの詳細値を表示する」。 */}
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.06)' }}
              content={renderTooltipContent}
              isAnimationActive={false}
            />

            {/* 発電形式内訳の積み上げ Bar は無い: この画面 (単体BG詳細) の
                発電形式トグルの新設は #2807 (積み上げデータ整備) とセットの
                別スコープで #7537 の対象外 (Done 定義)。 旧実装は常に null
                固定の 5 区分プレースホルダー Bar を描いていたが、 データが
                常に無いので実質何も描画しない到達不能コードだった
                (2026-09-10 是正)。 */}

            {/* Plan / Actual Lines (基本表示トグル連動) */}
            {seriesVisibility.genPlan && (
              <Line
                stroke={planColor}
                dataKey="genPlan"
                dot={false}
                isAnimationActive={false}
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {seriesVisibility.genActual && (
              <Line
                stroke={actualColor}
                dataKey="genActual"
                dot={false}
                isAnimationActive={false}
                strokeWidth={2}
                type="stepAfter"
              />
            )}

            {/* GC 縦線 + ラベル (#2941)。 AC-BDH-019a により当日のみ。
                ラベルは design `js/charts.js` renderHatsuChart と同じく
                `ReferenceLine` の `label` に持たせ、 **赤い破線の真上**に出す
                (insideTopRight / 11px / 700。 2026-08-25 デザイナー指摘)。

                当初は需要詳細と同じ共通 [[ChartMarkerLabels]] でプロット右上に
                固定していたが、 それだと線が左寄りのときラベルと線が離れて
                「どの線のラベルか」 が読めない。 線に付いていることが優先。 */}
            {isTodayDisplay && (
              <ReferenceLine
                /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh` (#870103) は
                   暗いマルーンで、 design の GC 色 (`js/charts.js:876` `cssVar('surface-negative-high')`)
                   と別物 (2026-09-03 デザイナー指摘 #7064)。 線・ラベル・バッジで同じ 1 色を使う。 */
                stroke={LightModeSemanticColors.Surface.negativeHigh.value}
                label={{
                  value: `${t('page:balanceGenerationDetail.chart.gc')} ${gcTime}`,
                  position: 'insideTopRight',
                  fill: LightModeSemanticColors.Surface.negativeHigh.value,
                  fontSize: 11,
                  fontWeight: 700,
                }}
                strokeDasharray="4 4"
                strokeWidth={2}
                x={gcTime}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </div>
  )
}
